const OUTCOME_LABELS: Record<string, string> = {
  calling: "In progress",
  answered: "Picked up, no handoff",
  voicemail: "Message completed",
  no_answer: "No answer / busy",
  transferred: "Transferred",
  transfer_failed: "Transfer failed",
  failed: "Failed",
};

const REASON_LABELS: Record<string, string> = {
  not_now: "Not available now",
  wrong_number: "Wrong number",
  user_requested_dnc: "Asked to stop calls",
  stop_requested: "Asked to stop calls",
  user_hangup: "Recipient hung up",
  agent_hangup: "Agent ended call",
  inactivity: "Inactivity; human answer unconfirmed",
  max_duration_reached: "Time limit reached; human answer unconfirmed",
  silence_timeout: "Silence timeout",
  voicemail_reached: "Voicemail reached",
  ivr_reached: "Phone menu reached",
  voicemail: "Voicemail message completed",
  dial_no_answer: "No answer",
  dial_busy: "Line busy",
  user_declined: "Call declined",
  handoff_failed: "Navigator handoff did not connect",
  handoff_timeout: "Navigator handoff timed out",
  transfer_cancelled: "Transfer cancelled",
  call_transfer: "Transferred to navigator's line",
  transfer_bridged: "Transfer bridged",
  daily_preparation_failed: "Call preparation failed",
  call_ended_not_received: "Call end confirmation not received",
};

const CALL_PATH_LABELS: Record<string, string> = {
  person: "Person",
  ai_screener: "AI screener",
  phone_menu: "Phone menu",
  voicemail: "Voicemail",
  put_through: "Put through",
  sent_to_voicemail: "Sent to voicemail",
  ended: "Ended",
  navigator_never_joined: "Navigator never joined",
  navigator_left: "Navigator left",
  audio_not_ready: "Navigator audio not ready",
  family_hung_up: "Family hung up",
};

export function switchboardLabel(value: string): string {
  if (value === "daily") return "Daily";
  if (value === "retell") return "Retell";
  if (value === "calling") return "In progress";
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function switchboardOutcomeLabel(outcome: string): string {
  return OUTCOME_LABELS[outcome] ?? switchboardLabel(outcome);
}

export function switchboardReasonLabel(reason: string | null | undefined, outcome: string): string {
  if (!reason || reason === "unknown")
    return outcome === "calling" ? "Call in progress" : "Reason not recorded";
  return REASON_LABELS[reason] ?? switchboardLabel(reason);
}

export function switchboardAnswerKindLabel(kind: string) {
  const labels: Record<string, string> = {
    human: "Human",
    voicemail: "Voicemail system",
    screening: "Screening / menu",
    unknown: "Unknown answer type",
  };
  return labels[kind] ?? switchboardLabel(kind);
}

export function switchboardCallPathLabel(
  value: string | null,
  step: "firstAnswerer" | "screenerDecision" | "handoffFailureReason",
  missingLabel = "Not recorded",
) {
  if (value === null) return missingLabel;
  if (value === "unknown") return "Unknown";
  if (value === "not_applicable") {
    if (step === "screenerDecision") return "No screener decision";
    if (step === "handoffFailureReason") return "No handoff failure";
    return "Not applicable";
  }
  return CALL_PATH_LABELS[value] ?? switchboardLabel(value);
}

export function switchboardAnswerStage(outcome: string, answerKind?: string) {
  if (outcome === "calling") return { key: "calling", label: "In progress" };
  if (answerKind && answerKind !== "unknown")
    return { key: answerKind, label: switchboardAnswerKindLabel(answerKind) };
  if (outcome === "no_answer") return { key: "no_answer", label: "No answer / busy" };
  if (outcome === "failed") return { key: "failed", label: "Failed / unknown" };
  return { key: "unknown", label: "Unknown answer type" };
}

export function switchboardFlowResult(
  outcome: string,
  reason: string | null | undefined,
  answerKind?: string,
  voicemailDelivered?: boolean | null,
) {
  if (answerKind === "voicemail" && outcome === "answered" && !voicemailDelivered)
    return {
      key: "voicemail_incomplete",
      label: "Complete message not recorded",
      detail: "Voicemail system reached",
    };
  const reasonLabel = switchboardReasonLabel(reason, outcome);
  const specificAnswer =
    outcome === "answered" &&
    ["not_now", "wrong_number", "user_requested_dnc", "stop_requested"].includes(reason ?? "");
  return {
    key: JSON.stringify([outcome, reason || null]),
    label: specificAnswer ? reasonLabel : switchboardOutcomeLabel(outcome),
    detail: specificAnswer ? "No handoff" : reasonLabel,
  };
}
